/** @jsxImportSource @emotion/react */
import { Global } from "@emotion/react";
import { useState, useEffect, useRef } from "react";
import { useParams, useLocation } from "react-router-dom";
import {
  accessAPI,
  downloadFile,
  formatFecha,
  Titulo,
  EstadoCertificado as EstadoCertificadoBanner,
  CampoInformacion, tituloBase } from "../../shared";
import { Boton, Sidebar, TablaFiltrable } from "../../shared";
import { Skeleton } from "primereact/skeleton";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { Timeline } from "primereact/timeline";
import { Tag } from "primereact/tag";
import { Menu } from "primereact/menu";
import { Tooltip } from "primereact/tooltip";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { Toast } from "primereact/toast";
import { Message } from "primereact/message";
import { Formulario } from "../../shared";
import { useContextoAsistente } from "../asistente/ContextoAsistente";

export default function Certificado() {
  const [loader, setLoader] = useState(true);

  const [certificado, setCertificado] = useState();
  const [emisiones, setEmisiones] = useState();

  const [loaderDescargaCertificado, setLoaderDescargaCertificado] =
    useState(false);
  const [cambiosEstado, setCambiosEstado] = useState([]);
  const [sidebarCambioEstado, setSidebarCambioEstado] = useState(false);
  const [estadosDropdown, setEstadosDropdown] = useState();
  const [loaderCambioEstado, setLoaderCambioEstado] = useState(false);
  const [estadoSeleccionado, setEstadoSeleccionado] = useState(null);

  const [extensiones, setExtensiones] = useState([]);
  const [sidebarExtension, setSidebarExtension] = useState(false);
  const [loaderExtension, setLoaderExtension] = useState(false);

  const [sidebarAcciones, setSidebarAcciones] = useState(false);
  const [loaderRegenerarCertificado, setLoaderRegenerarCertificado] =
    useState(false);
  const [loaderRegenerarEtiquetas, setLoaderRegenerarEtiquetas] =
    useState(false);
  const [loaderReemision, setLoaderReemision] = useState(false);
  const [sidebarReemision, setSidebarReemision] = useState(false);

  const [emisionSeleccionada, setEmisionSeleccionada] = useState(null);
  const [sidebarEmision, setSidebarEmision] = useState(false);
  const [loaderEnvioUrsea, setLoaderEnvioUrsea] = useState(false);
  const [loaderDescargaXml, setLoaderDescargaXml] = useState(false);

  const [sidebarEditarDetalles, setSidebarEditarDetalles] = useState(false);
  const [loaderEditarDetalles, setLoaderEditarDetalles] = useState(false);

  const [sidebarBajaUrsea, setSidebarBajaUrsea] = useState(false);
  const [loaderBajaUrsea, setLoaderBajaUrsea] = useState(false);
  const [cambioEstadoParaBaja, setCambioEstadoParaBaja] = useState(null);

  const toast = useRef(null);
  const menuRefsExt = useRef({});

  // Carga el id del certificado de la URL
  const { certificadoId } = useParams();
  useContextoAsistente({
    tipo: "certificado",
    id: certificadoId,
    nombre: certificado?.numero
  });
  const location = useLocation();

  function cargarCertificado() {
    accessAPI(
      "GET",
      `admin/certificado/${certificadoId}`,
      null,
      (respuesta) => {
        setCertificado(respuesta.certificado);
        setEmisiones(respuesta.certificado.emisioncertificado);
        document.title = respuesta.certificado.numero;
      },
      (respuesta) => {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al cargar el certificado",
          sticky: true
        });
      }
    );
  }

  // Cuando carga el componente va a buscar el certificado
  useEffect(() => {
    if (certificadoId) {
      cargarCertificado();
    }
    return () => (document.title = tituloBase());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Si llega desde la emisión con errores parciales (PDF, etiquetas, etc.),
  // los muestra y limpia el estado de navegación para no repetirlos al recargar
  useEffect(() => {
    if (location.state?.erroresEmision?.length) {
      toast.current?.show({
        severity: "warn",
        summary: "Certificado emitido con errores",
        detail: location.state.erroresEmision.join(". "),
        sticky: true
      });
      window.history.replaceState({}, "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cuando carga el certificado apaga el loader
  useEffect(() => {
    if (certificado) {
      setLoader(false);
      setCambiosEstado(certificado.cambioestadocertificado || []);
      setExtensiones(certificado.extensioncertificado || []);
    }
  }, [certificado]);

  // Carga los estados disponibles para el dropdown
  useEffect(() => {
    accessAPI(
      "GET",
      "admin/config/estadocertificado?dropdown=true",
      null,
      (respuesta) => setEstadosDropdown(respuesta),
      () => {}
    );
  }, []);

  // Genera y descarga la marca de conformidad
  function descargarMarca() {
    setLoaderDescargaCertificado(true);
    downloadFile(
      `admin/certificado/descargarmarca/${certificadoId}`,
      `Marca de conformidad ${certificado.numero}.pdf`,
      () => setLoaderDescargaCertificado(false),
      (respuesta) => {
        setLoaderDescargaCertificado(false);
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al descargar marca",
          sticky: true
        });
      }
    );
  }

  // Genera la etiqueta de datos del importador para el empaque de RSPEBT
  function descargarEtiquetaDatosImportador() {
    setLoaderDescargaCertificado(true);
    downloadFile(
      `admin/certificado/descargaretiquetadatos/${certificadoId}`,
      `Etiqueta datos cliente ${certificado.numero}.pdf`,
      () => setLoaderDescargaCertificado(false),
      (respuesta) => {
        setLoaderDescargaCertificado(false);
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al descargar etiqueta de datos",
          sticky: true
        });
      }
    );
  }

  // Descarga el certificado de la última emisión
  function descargarCertificado() {
    const ultimaEmision = emisiones?.[0];
    if (!ultimaEmision) {
      toast.current.show({
        severity: "warn",
        summary: "Sin certificado",
        detail: "El certificado todavía no tiene emisiones para descargar.",
        sticky: true
      });
      return;
    }
    if (!ultimaEmision.archivopdf?.id) {
      toast.current.show({
        severity: "warn",
        summary: "Sin PDF",
        detail:
          "La última emisión no tiene el PDF del certificado: probablemente falló su generación. Regeneralo desde Acciones → Regenerar certificado.",
        sticky: true
      });
      return;
    }
    setLoaderDescargaCertificado(true);
    downloadFile(
      `admin/certificado/descargarpdfemision/${ultimaEmision.id}`,
      `Certificado ${certificado.numero}.pdf`,
      () => setLoaderDescargaCertificado(false),
      (respuesta) => {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al descargar",
          sticky: true
        });
        setLoaderDescargaCertificado(false);
      }
    );
  }

  function descargarPdfExtension(ext) {
    const sufijo = ext.numeroextension ? ` (ext ${ext.numeroextension})` : "";
    downloadFile(
      `admin/certificado/descargarpdfextension/${ext.id}`,
      `Certificado ${certificado.numero}${sufijo}.pdf`,
      () => {},
      (respuesta) => {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al descargar",
          sticky: true
        });
      }
    );
  }

  function enviarExtensionAUrsea(ext) {
    const codigo = `${certificado.numero}/${ext.numeroextension}`;
    confirmDialog({
      message: ext.fechaenvioursea
        ? `Esta extensión ya fue enviada a URSEA el ${formatFecha(ext.fechaenvioursea)}. ¿Reenviarla como un alta nueva (${codigo}) a nombre de "${ext.clienteextendido?.razonsocial}"?`
        : `¿Enviar la extensión ${codigo} a URSEA? Se genera un alta nueva a nombre de "${ext.clienteextendido?.razonsocial}".`,
      header: "Enviar extensión a URSEA",
      icon: "pi pi-send",
      acceptLabel: "Enviar",
      rejectLabel: "Cancelar",
      accept: () => {
        marcarEnviandoUrsea(ext.id, true);
        accessAPI(
          "PUT",
          `admin/certificado/enviarurseaextension/${ext.id}`,
          null,
          (respuesta) => {
            cargarCertificado();
            toast.current.show({
              severity: "success",
              summary: "Listo",
              detail: respuesta[0]?.msg || "Extensión enviada a URSEA",
              life: 4000
            });
          },
          (respuesta) => {
            marcarEnviandoUrsea(ext.id, false);
            toast.current.show({
              severity: "error",
              summary: "Error",
              detail: respuesta[0]?.msg || "Error al enviar la extensión a URSEA",
              sticky: true
            });
          }
        );
      }
    });
  }

  function descargarXmlExtension(ext) {
    downloadFile(
      `admin/certificado/descargarxmlextension/${ext.id}`,
      ext.archivoxml?.nombredescarga || "certificado-extension.xml",
      () => {},
      (respuesta) => {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al descargar XML",
          sticky: true
        });
      }
    );
  }

  function marcarEnviandoUrsea(extensionId, valor) {
    setExtensiones((prev) =>
      prev.map((e) =>
        e.id === extensionId ? { ...e, _enviandoUrsea: valor } : e
      )
    );
  }

  function handleCambioEstado(campos) {
    setLoaderCambioEstado(true);
    accessAPI(
      "PUT",
      `admin/certificado/${certificadoId}/cambioestado`,
      campos,
      (respuesta) => {
        setCertificado((prev) => ({
          ...prev,
          estadocertificado: respuesta.nuevoestado,
          cambioestadocertificado: [
            respuesta,
            ...(prev.cambioestadocertificado || [])
          ]
        }));
        setSidebarCambioEstado(false);
        setLoaderCambioEstado(false);
        setEstadoSeleccionado(null);
        toast.current.show({
          severity: "success",
          summary: "Listo",
          detail: "Estado actualizado",
          life: 3000
        });
      },
      (respuesta) => {
        setLoaderCambioEstado(false);
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al cambiar estado",
          sticky: true
        });
      }
    );
  }

  function handleEditarDetalles(campos) {
    setLoaderEditarDetalles(true);
    accessAPI(
      "PUT",
      `admin/certificado/${certificadoId}`,
      campos,
      (respuesta) => {
        setCertificado((prev) => ({
          ...prev,
          fechavencimiento: respuesta.fechavencimiento
        }));
        setSidebarEditarDetalles(false);
        setLoaderEditarDetalles(false);
        toast.current.show({
          severity: "success",
          summary: "Listo",
          detail: "Certificado actualizado",
          life: 3000
        });
      },
      (respuesta) => {
        setLoaderEditarDetalles(false);
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al actualizar certificado",
          sticky: true
        });
      }
    );
  }

  function handleBajaUrsea(campos) {
    setLoaderBajaUrsea(true);
    accessAPI(
      "PUT",
      `admin/certificado/enviarurseabaja/${certificadoId}`,
      { motivo: campos.motivo },
      () => {
        const ahora = new Date().toISOString();
        setCambiosEstado((prev) =>
          prev.map((c) =>
            c.id === cambioEstadoParaBaja.id
              ? { ...c, fechabajaursea: ahora }
              : c
          )
        );
        setSidebarBajaUrsea(false);
        setLoaderBajaUrsea(false);
        setCambioEstadoParaBaja(null);
        toast.current.show({
          severity: "success",
          summary: "Listo",
          detail: "Baja enviada a URSEA",
          life: 3000
        });
      },
      (respuesta) => {
        setLoaderBajaUrsea(false);
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al enviar baja a URSEA",
          sticky: true
        });
      }
    );
  }

  function handleNuevaExtension(campos) {
    setLoaderExtension(true);
    accessAPI(
      "POST",
      `admin/certificado/extension/${certificadoId}`,
      campos,
      (respuesta) => {
        setExtensiones((prev) => [...prev, respuesta]);
        setSidebarExtension(false);
        setLoaderExtension(false);
        toast.current.show({
          severity: "success",
          summary: "Listo",
          detail: "Extensión generada",
          life: 3000
        });
      },
      (respuesta) => {
        setLoaderExtension(false);
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al generar extensión",
          sticky: true
        });
      }
    );
  }

  function cancelarExtension(extensionId) {
    accessAPI(
      "PUT",
      `admin/certificado/extension/${extensionId}`,
      null,
      () => {
        setExtensiones((prev) =>
          prev.map((e) =>
            e.id === extensionId
              ? { ...e, activo: false, fechabaja: new Date().toISOString() }
              : e
          )
        );
        toast.current.show({
          severity: "success",
          summary: "Listo",
          detail: "Extensión cancelada",
          life: 3000
        });
      },
      (respuesta) => {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al cancelar extensión",
          sticky: true
        });
      }
    );
  }

  // Aprueba una extensión pendiente: genera el PDF y la hace efectiva (recién ahí
  // la ve el cliente). Antes consulta el estado de la factura y avisa si NO está paga.
  function aprobarExtension(extensionId) {
    const marcarAprobando = (valor) =>
      setExtensiones((prev) =>
        prev.map((e) =>
          e.id === extensionId ? { ...e, _aprobando: valor } : e
        )
      );

    const ejecutarAprobacion = () => {
      // Muestra el loader en la fila: la aprobación genera el PDF y demora.
      marcarAprobando(true);
      accessAPI(
        "PUT",
        `admin/certificado/extension/${extensionId}/aprobar`,
        null,
        () => {
          // Recarga el certificado para reflejar el estado real (ícono → Activa).
          // El reload reemplaza las filas, limpiando el flag _aprobando.
          cargarCertificado();
          toast.current.show({
            severity: "success",
            summary: "Listo",
            detail: "Extensión aprobada",
            life: 3000
          });
        },
        (respuesta) => {
          marcarAprobando(false);
          toast.current.show({
            severity: "error",
            summary: "Error",
            detail: respuesta[0]?.msg || "Error al aprobar la extensión",
            sticky: true
          });
        }
      );
    };

    const efectiva =
      "Al aprobar se genera el PDF y la extensión se hace efectiva (visible para el cliente).";

    accessAPI(
      "GET",
      `admin/certificado/extension/${extensionId}/factura-pagada`,
      null,
      (estado) => {
        let message;
        if (estado.pagada) {
          message = `La factura de la extensión está paga. ${efectiva} ¿Continuar?`;
        } else if (estado.facturada) {
          message = `La factura de esta extensión NO está paga. ${efectiva} ¿Desea continuar igual?`;
        } else {
          message = `Esta extensión todavía no tiene factura emitida. ${efectiva} ¿Desea continuar igual?`;
        }
        confirmDialog({
          message,
          header: "Aprobar extensión",
          icon: estado.pagada ? "pi pi-check" : "pi pi-exclamation-triangle",
          acceptLabel: "Aprobar",
          rejectLabel: "Cancelar",
          acceptClassName: estado.pagada ? undefined : "p-button-warning",
          accept: ejecutarAprobacion
        });
      },
      () => {
        confirmDialog({
          message: `No se pudo verificar el estado de la factura. ${efectiva} ¿Continuar?`,
          header: "Aprobar extensión",
          icon: "pi pi-exclamation-triangle",
          acceptLabel: "Aprobar",
          rejectLabel: "Cancelar",
          accept: ejecutarAprobacion
        });
      }
    );
  }

  function regenerarCertificado() {
    if (!emisiones || emisiones.length === 0) return;
    setLoaderRegenerarCertificado(true);
    accessAPI(
      "PUT",
      `admin/certificado/regenerar/${emisiones[0].id}`,
      null,
      (respuesta) => {
        setLoaderRegenerarCertificado(false);
        toast.current.show({
          severity: "success",
          summary: "Listo",
          detail: respuesta[0]?.msg || "Certificado regenerado",
          life: 3000
        });
        // Trae la emisión con su PDF nuevo: sin esto, la descarga seguiría
        // viendo la emisión sin archivo.
        cargarCertificado();
      },
      (respuesta) => {
        setLoaderRegenerarCertificado(false);
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al regenerar certificado",
          sticky: true
        });
      }
    );
  }

  function regenerarEtiquetas() {
    setLoaderRegenerarEtiquetas(true);
    accessAPI(
      "PUT",
      `admin/certificado/generaretiquetas/${certificadoId}`,
      null,
      (respuesta) => {
        setLoaderRegenerarEtiquetas(false);
        toast.current.show({
          severity: "success",
          summary: "Listo",
          detail: respuesta[0]?.msg || "Etiquetas regeneradas",
          life: 3000
        });
      },
      (respuesta) => {
        setLoaderRegenerarEtiquetas(false);
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al regenerar etiquetas",
          sticky: true
        });
      }
    );
  }

  function reemitirCertificado(campos) {
    setLoaderReemision(true);
    accessAPI(
      "PUT",
      `admin/certificado/reemitir/${certificadoId}`,
      campos,
      (respuesta) => {
        setLoaderReemision(false);
        setSidebarReemision(false);
        setEmisiones((prev) => [respuesta, ...prev]);
        if (respuesta.erroresEmision?.length) {
          toast.current.show({
            severity: "warn",
            summary: "Certificado reemitido con errores",
            detail: respuesta.erroresEmision.join(". "),
            sticky: true
          });
        } else {
          toast.current.show({
            severity: "success",
            summary: "Listo",
            detail: "Certificado reemitido",
            life: 3000
          });
        }
      },
      (respuesta) => {
        setLoaderReemision(false);
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al reemitir certificado",
          sticky: true
        });
      }
    );
  }

  function enviarAUrsea() {
    if (!emisionSeleccionada) return;
    setLoaderEnvioUrsea(true);
    accessAPI(
      "PUT",
      `admin/certificado/enviarurseaemision/${emisionSeleccionada.id}`,
      null,
      (respuesta) => {
        setLoaderEnvioUrsea(false);
        const ahora = new Date().toISOString();
        setEmisionSeleccionada((prev) => ({
          ...prev,
          fechaenvioursea: ahora,
          archivoxml: respuesta.archivoXML || prev.archivoxml
        }));
        setEmisiones((prev) =>
          prev.map((e) =>
            e.id === emisionSeleccionada.id
              ? { ...e, fechaenvioursea: ahora, archivoxml: respuesta.archivoXML || e.archivoxml }
              : e
          )
        );
        toast.current.show({
          severity: "success",
          summary: "Listo",
          detail: respuesta.msg || "Enviado a URSEA",
          life: 3000
        });
      },
      (respuesta) => {
        setLoaderEnvioUrsea(false);
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al enviar a URSEA",
          sticky: true
        });
      }
    );
  }

  function descargarXmlEmision() {
    if (!emisionSeleccionada?.archivoxml) return;
    setLoaderDescargaXml(true);
    downloadFile(
      `admin/certificado/descargarxmlemision/${emisionSeleccionada.id}`,
      emisionSeleccionada.archivoxml.nombredescarga || "certificado.xml",
      () => setLoaderDescargaXml(false),
      (respuesta) => {
        setLoaderDescargaXml(false);
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al descargar XML",
          sticky: true
        });
      }
    );
  }

  function getCertificadoEstado() {
    if (!certificado) return { pendiente: true };
    if (certificado.estadocertificado.vigentebool) {
      return { vigente: true, fechaEmision: certificado.fechaemision };
    } else if (certificado.estadocertificado.bajadefinitiva) {
      return { anulado: true };
    }
    return { suspendido: true };
  }

  // Mostramos la Card si existe al menos una extensión, incluyendo las
  // dadas de baja (activo === false).
  const hayExtensiones = extensiones.length > 0;

  return (
    <>
      <Toast ref={toast} position="top-center" baseZIndex={2000} />
      <ConfirmDialog />
      {loader && (
        <div>
          <div
            css={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "2em",
              marginBottom: "1em"
            }}
          >
            <Skeleton width="22rem" height="2rem" />
            <div css={{ display: "flex", gap: "0.5rem" }}>
              <Skeleton width="6rem" height="2.5rem" />
              <Skeleton width="10rem" height="2.5rem" />
            </div>
          </div>
          <div className="grid" css={{ marginTop: "1em" }}>
            <div
              className="col-12 lg:col-6"
              css={{ display: "flex", flexDirection: "column", gap: "1em" }}
            >
              <Card>
                <div
                  css={{ display: "flex", alignItems: "center", gap: "1rem" }}
                >
                  <Skeleton shape="circle" size="3rem" />
                  <Skeleton width="8rem" height="1.2rem" />
                </div>
              </Card>
              <Card>
                <Skeleton
                  width="6rem"
                  height="1.5rem"
                  css={{ marginBottom: "1em" }}
                />
                <div className="grid">
                  {[1, 2, 3].map((i) => (
                    <div className="col-12 md:col-6" key={i}>
                      <Skeleton
                        width="5rem"
                        height="0.8rem"
                        css={{ marginBottom: "0.3rem" }}
                      />
                      <Skeleton width="8rem" height="1.1rem" />
                    </div>
                  ))}
                </div>
              </Card>
              <Card>
                <Skeleton
                  width="8rem"
                  height="1.5rem"
                  css={{ marginBottom: "1em" }}
                />
                <Skeleton
                  width="100%"
                  height="2.5rem"
                  css={{ marginBottom: "0.3rem" }}
                />
                <Skeleton width="100%" height="2.5rem" />
              </Card>
            </div>
            <div className="col-12 lg:col-6">
              <Card>
                <Skeleton
                  width="7rem"
                  height="1.5rem"
                  css={{ marginBottom: "1.5em" }}
                />
                {[1, 2].map((i) => (
                  <div
                    key={i}
                    css={{
                      display: "flex",
                      gap: "1rem",
                      alignItems: "center",
                      marginBottom: "1rem"
                    }}
                  >
                    <Skeleton shape="circle" size="2rem" />
                    <div css={{ flex: 1 }}>
                      <Skeleton
                        width="70%"
                        height="1rem"
                        css={{ marginBottom: "0.3rem" }}
                      />
                      <Skeleton width="50%" height="0.8rem" />
                    </div>
                  </div>
                ))}
              </Card>
              <Card css={{ marginTop: "1em" }}>
                <Skeleton
                  width="10rem"
                  height="1.5rem"
                  css={{ marginBottom: "1.5em" }}
                />
                <div
                  css={{ display: "flex", gap: "1rem", alignItems: "center" }}
                >
                  <Skeleton shape="circle" size="2rem" />
                  <div css={{ flex: 1 }}>
                    <Skeleton
                      width="60%"
                      height="1rem"
                      css={{ marginBottom: "0.3rem" }}
                    />
                    <Skeleton width="40%" height="0.8rem" />
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      )}
      {!loader && (
        <>
          <Titulo
            urlVolver={`/clientes/${certificado.proceso.cliente.id}/procesos`}
            subtitulo={`${certificado.proceso.cliente.razonsocial} - ${certificado.proceso.nombrefantasia}`}
            botones={
              <>
                <Boton transparente callback={() => setSidebarAcciones(true)}>
                  Acciones
                </Boton>
                <Boton
                  callback={descargarCertificado}
                  loader={loaderDescargaCertificado}
                  menu={[
                    {
                      etiqueta: "Descargar marca",
                      icono: "pi pi-verified",
                      callback: descargarMarca
                    },
                    {
                      etiqueta: "Descargar etiqueta de datos del importador",
                      icono: "pi pi-tag",
                      callback: descargarEtiquetaDatosImportador
                    }
                  ]}
                >
                  Descargar certificado
                </Boton>
              </>
            }
          >
            {`Proceso ${certificado.proceso.nombre} - Certificado`}
          </Titulo>
          <div className="grid" css={{ marginTop: "1em" }}>
            <div
              className="col-12 lg:col-6"
              css={{ display: "flex", flexDirection: "column", gap: "1em" }}
            >
              <EstadoCertificadoBanner
                {...getCertificadoEstado()}
                sinRenovacion={certificado?.proceso?.certificadotipo?.sinrenovacion}
              />
              <Card>
                <Titulo
                  botones={
                    <Boton
                      texto
                      negro
                      callback={() => setSidebarEditarDetalles(true)}
                    >
                      <i className="pi pi-pencil" />
                    </Boton>
                  }
                >
                  Detalles
                </Titulo>
                <div className="grid">
                  <CampoInformacion nombreCampo="Estado">
                    {certificado.estadocertificado.nombre}
                  </CampoInformacion>
                  <CampoInformacion nombreCampo="Fecha vencimiento">
                    {formatFecha(certificado.fechavencimiento)}
                  </CampoInformacion>
                  {certificado.proceso.certificadotipo.gestionadoursea &&
                    certificado.pinenvioursea && (
                      <CampoInformacion nombreCampo="PIN envío a URSEA">
                        {certificado.pinenvioursea}
                      </CampoInformacion>
                    )}
                </div>
              </Card>
              <Sidebar
                visible={sidebarEditarDetalles}
                onHide={() => setSidebarEditarDetalles(false)}
                position="right"
                className="w-full lg:w-30rem md:w-30rem"
              >
                <h2>Editar detalles</h2>
                <Formulario
                  config={{
                    secciones: [
                      {
                        campos: [
                          {
                            nombre: "fechavencimiento",
                            tipoitemformularioid: 7,
                            etiqueta: "Fecha de vencimiento",
                            requerido: true,
                            respuesta: certificado.fechavencimiento
                              ? new Date(certificado.fechavencimiento)
                              : null
                          }
                        ]
                      }
                    ],
                    botones: [
                      {
                        etiqueta: "Guardar",
                        callback: handleEditarDetalles,
                        validar: true
                      }
                    ]
                  }}
                  loader={loaderEditarDetalles}
                  key={certificado.fechavencimiento}
                />
              </Sidebar>
              {hayExtensiones && (
              <Card>
                <Titulo
                  botones={
                    <Boton
                      texto
                      negro
                      callback={() => setSidebarExtension(true)}
                    >
                      <i className="pi pi-plus" />
                    </Boton>
                  }
                >
                  Extensiones
                </Titulo>
                <Global
                  styles={{
                    ".ext-estado-tooltip .p-tooltip-text": {
                      whiteSpace: "pre-line"
                    }
                  }}
                />
                <Tooltip
                  key={extensiones.map((e) => e.id).join(",")}
                  target=".ext-estado-icon"
                  className="ext-estado-tooltip"
                />
                <TablaFiltrable conFiltros={false}
                  value={extensiones}
                  emptyMessage="No hay extensiones"
                  size="small"
                  nombreArchivo="extensiones_certificado"
                >
                  <Column
                    header="Número"
                    body={(ext) => ext.numeroextension ?? "—"}
                    style={{ width: "80px" }}
                  />
                  <Column
                    header="Cliente"
                    body={(ext) => ext.clienteextendido?.razonsocial}
                  />
                  {certificado.proceso.certificadotipo.gestionadoursea && (
                    <Column
                      header="PIN envío a URSEA"
                      body={(ext) => ext.pinenvioursea || "—"}
                      style={{ width: "140px" }}
                    />
                  )}
                  <Column
                    body={(ext) => {
                      // Mientras se aprueba (genera el PDF) o se envía a URSEA
                      // (llamada SOAP que demora) mostramos un loader.
                      if (ext._aprobando || ext._enviandoUrsea) {
                        return (
                          <i
                            className="ext-estado-icon pi pi-spin pi-spinner"
                            data-pr-tooltip={
                              ext._enviandoUrsea ? "Enviando a URSEA…" : "Aprobando…"
                            }
                            data-pr-position="top"
                            style={{
                              color: "var(--principal)",
                              fontSize: "1.1rem"
                            }}
                          />
                        );
                      }
                      let icono, color;
                      const lineas = [];
                      if (!ext.activo) {
                        icono = "pi pi-times-circle";
                        color = "var(--rojo)";
                        lineas.push("Dada de baja");
                        lineas.push(`Desde: ${formatFecha(ext.fechaextension)}`);
                        if (ext.fechabaja) {
                          lineas.push(`Hasta: ${formatFecha(ext.fechabaja)}`);
                        }
                      } else if (ext.aprobada) {
                        icono = "pi pi-check-circle";
                        color = "var(--verde)";
                        lineas.push("Activa");
                        lineas.push(`Desde: ${formatFecha(ext.fechaextension)}`);
                      } else {
                        icono = "pi pi-exclamation-triangle";
                        color = "var(--amarillo)";
                        lineas.push("Pendiente");
                      }
                      const tooltip = lineas.join("\n");
                      return (
                        <i
                          className={`ext-estado-icon ${icono}`}
                          data-pr-tooltip={tooltip}
                          data-pr-position="top"
                          style={{ color, fontSize: "1.1rem" }}
                        />
                      );
                    }}
                    style={{ width: "40px", textAlign: "center" }}
                  />
                  <Column
                    body={(ext) => {
                      if (!ext.activo) return null;
                      const items = [];
                      if (ext.archivopdfid) {
                        items.push({
                          label: "Descargar certificado",
                          icon: "pi pi-download",
                          command: () => descargarPdfExtension(ext)
                        });
                      }
                      if (
                        certificado.proceso.certificadotipo.gestionadoursea &&
                        ext.archivopdfid &&
                        !ext.fechaenvioursea
                      ) {
                        items.push({
                          label: "Enviar a URSEA",
                          icon: "pi pi-send",
                          command: () => enviarExtensionAUrsea(ext)
                        });
                      }
                      if (ext.archivoxmlid) {
                        items.push({
                          label: "Descargar XML URSEA",
                          icon: "pi pi-file",
                          command: () => descargarXmlExtension(ext)
                        });
                      }
                      if (!ext.aprobada) {
                        items.push({
                          label: "Aprobar extensión",
                          icon: "pi pi-check",
                          command: () => aprobarExtension(ext.id)
                        });
                      }
                      items.push({
                        label: "Dar de baja",
                        icon: "pi pi-trash",
                        command: () =>
                          confirmDialog({
                            message: "¿Está seguro de dar de baja esta extensión?",
                            header: "Dar de baja",
                            icon: "pi pi-exclamation-triangle",
                            acceptLabel: "Dar de baja",
                            rejectLabel: "Cancelar",
                            acceptClassName: "p-button-danger",
                            accept: () => cancelarExtension(ext.id)
                          })
                      });
                      // Info (no accionable) del envío a URSEA, separada del resto.
                      if (ext.fechaenvioursea) {
                        items.push({ separator: true });
                        items.push({
                          label: `Enviado a URSEA en ${formatFecha(ext.fechaenvioursea)}`,
                          icon: "pi pi-check-circle",
                          disabled: true
                        });
                      }
                      return (
                        <div>
                          <Menu
                            // El Menu popup de PrimeReact cachea su model una vez
                            // montado; con este key se remonta cuando cambian los
                            // ítems (ej. aparece "Descargar XML" tras enviar a URSEA).
                            key={items.map((i) => i.label).join("|")}
                            model={items}
                            popup
                            ref={(el) => (menuRefsExt.current[ext.id] = el)}
                            id={`menu_ext_${ext.id}`}
                          />
                          <Boton
                            icono
                            chico
                            negro
                            callback={(event) =>
                              menuRefsExt.current[ext.id]?.toggle(event)
                            }
                          >
                            <i className="pi pi-ellipsis-v"></i>
                          </Boton>
                        </div>
                      );
                    }}
                    style={{ width: "60px" }}
                  />
                </TablaFiltrable>
              </Card>
              )}

              <Sidebar
                visible={sidebarExtension}
                onHide={() => setSidebarExtension(false)}
                position="right"
                className="w-full lg:w-30rem md:w-30rem"
              >
                <h2>Nueva extensión</h2>
                <Formulario
                  config={{
                    secciones: [
                      {
                        campos: [
                          {
                            nombre: "clienteextendidoid",
                            tipoitemformularioid: 5,
                            etiqueta: "Cliente",
                            requerido: true,
                            opciones: "admin/cliente",
                            sinOpcionInicial: true
                          }
                        ]
                      }
                    ],
                    botones: [
                      {
                        etiqueta: "Generar extensión",
                        callback: handleNuevaExtension,
                        validar: true,
                        confirmacion: "¿Está seguro de generar esta extensión?"
                      }
                    ]
                  }}
                  loader={loaderExtension}
                  key={extensiones.length}
                />
              </Sidebar>
            </div>
            <div className="col-12 lg:col-6">
              <Card>
                <Titulo>Emisiones</Titulo>
                {emisiones.length === 0 && (
                  <p css={{ color: "var(--grisTextos)" }}>No hay emisiones</p>
                )}
                {emisiones.length > 0 && (
                  <Timeline
                    value={emisiones}
                    opposite={(emision) => (
                      <div css={{ marginTop: "10px" }}>
                        {formatFecha(emision.fechaemision)}
                      </div>
                    )}
                    content={(emision) => (
                      <Card
                        className="mb-3"
                        css={{
                          cursor: "pointer",
                          "&:hover": {
                            boxShadow: "0 2px 8px rgba(0,0,0,0.15)"
                          }
                        }}
                        onClick={() => {
                          setEmisionSeleccionada(emision);
                          setSidebarEmision(true);
                        }}
                      >
                        <div
                          css={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "0.75em"
                          }}
                        >
                          <strong>{emision.notas || "Emisión"}</strong>
                          {emision.actividad && (
                            <small className="text-500">
                              <strong>Actividad:</strong>{" "}
                              {emision.actividad.actividadtipo.nombre}
                            </small>
                          )}
                          {certificado.proceso.certificadotipo
                            .gestionadoursea && (
                            <small className="text-500">
                              <strong>URSEA:</strong>{" "}
                              {emision.fechaenvioursea
                                ? `Enviado ${formatFecha(emision.fechaenvioursea)}`
                                : "No enviado"}
                            </small>
                          )}
                          <div
                            css={{
                              display: "flex",
                              gap: "0.5em",
                              flexWrap: "wrap"
                            }}
                          >
                            {emision.productoalta?.length > 0 && (
                              <Tag
                                value={`${emision.productoalta.length} alta(s)`}
                                severity="success"
                                css={{ fontSize: "0.75rem" }}
                              />
                            )}
                            {emision.productobaja?.length > 0 && (
                              <Tag
                                value={`${emision.productobaja.length} baja(s)`}
                                severity="danger"
                                css={{ fontSize: "0.75rem" }}
                              />
                            )}
                            {emision.cambioproductocertificado?.length > 0 && (
                              <Tag
                                value={`${emision.cambioproductocertificado.length} cambio(s)`}
                                severity="warning"
                                css={{ fontSize: "0.75rem" }}
                              />
                            )}
                          </div>
                        </div>
                      </Card>
                    )}
                    marker={() => (
                      <span
                        css={{
                          display: "flex",
                          width: "2rem",
                          height: "2rem",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "50%",
                          backgroundColor: "var(--verde)",
                          color: "white",
                          fontSize: "0.8rem"
                        }}
                      >
                        <i className="pi pi-check" />
                      </span>
                    )}
                  />
                )}
              </Card>

              <Sidebar
                visible={sidebarEmision}
                onHide={() => setSidebarEmision(false)}
                position="right"
                className="w-full lg:w-30rem md:w-30rem"
              >
                {emisionSeleccionada && (
                  <div
                    css={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "1em"
                    }}
                  >
                    <h2 css={{ margin: 0 }}>
                      {emisionSeleccionada.notas || "Emisión"}
                    </h2>
                    <div
                      css={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.5em"
                      }}
                    >
                      <CampoInformacion nombreCampo="Fecha de emisión">
                        {formatFecha(emisionSeleccionada.fechaemision)}
                      </CampoInformacion>
                      {emisionSeleccionada.actividad && (
                        <CampoInformacion nombreCampo="Actividad">
                          {emisionSeleccionada.actividad.actividadtipo.nombre}
                        </CampoInformacion>
                      )}
                      {certificado.proceso.certificadotipo.gestionadoursea && (
                        <CampoInformacion nombreCampo="Envío URSEA">
                          {emisionSeleccionada.fechaenvioursea
                            ? formatFecha(emisionSeleccionada.fechaenvioursea)
                            : "No enviado"}
                        </CampoInformacion>
                      )}
                    </div>

                    {certificado.proceso.certificadotipo.gestionadoursea && (
                      <div css={{ display: "flex", gap: "0.5em", flexWrap: "wrap" }}>
                        {!emisionSeleccionada.fechaenvioursea && (
                          <Boton
                            callback={enviarAUrsea}
                            loader={loaderEnvioUrsea}
                            confirmacion="¿Está seguro de enviar esta emisión a URSEA?"
                          >
                            Enviar a URSEA
                          </Boton>
                        )}
                        {emisionSeleccionada.archivoxml && (
                          <Boton
                            transparente
                            callback={descargarXmlEmision}
                            loader={loaderDescargaXml}
                          >
                            <i className="pi pi-download" css={{ marginRight: "0.5em" }} />
                            Descargar XML
                          </Boton>
                        )}
                      </div>
                    )}

                    {emisionSeleccionada.productoalta?.length > 0 && (
                      <div>
                        <h3
                          css={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5em"
                          }}
                        >
                          <Tag value="Altas" severity="success" />
                          Productos dados de alta
                        </h3>
                        <TablaFiltrable conFiltros={false}
                          value={emisionSeleccionada.productoalta}
                          size="small"
                          nombreArchivo="productos_alta"
                        >
                          <Column
                            header="#"
                            body={(_, opts) => opts.rowIndex + 1}
                            style={{ width: "40px" }}
                          />
                          <Column
                            header="Campos"
                            body={(producto) =>
                              producto.producto_x_productocampospropios
                                ?.map(
                                  (c) =>
                                    `${c.productocampospropios.etiqueta}: ${c.valor || "—"}`
                                )
                                .join(", ") || "—"
                            }
                          />
                        </TablaFiltrable>
                      </div>
                    )}

                    {emisionSeleccionada.productobaja?.length > 0 && (
                      <div>
                        <h3
                          css={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5em"
                          }}
                        >
                          <Tag value="Bajas" severity="danger" />
                          Productos dados de baja
                        </h3>
                        <TablaFiltrable conFiltros={false}
                          value={emisionSeleccionada.productobaja}
                          size="small"
                          nombreArchivo="productos_baja"
                        >
                          <Column
                            header="#"
                            body={(_, opts) => opts.rowIndex + 1}
                            style={{ width: "40px" }}
                          />
                          <Column
                            header="Campos"
                            body={(producto) =>
                              producto.producto_x_productocampospropios
                                ?.map(
                                  (c) =>
                                    `${c.productocampospropios.etiqueta}: ${c.valor || "—"}`
                                )
                                .join(", ") || "—"
                            }
                          />
                        </TablaFiltrable>
                      </div>
                    )}

                    {emisionSeleccionada.cambioproductocertificado?.length >
                      0 && (
                      <div>
                        <h3
                          css={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5em"
                          }}
                        >
                          <Tag value="Cambios" severity="warning" />
                          Cambios en productos
                        </h3>
                        <TablaFiltrable conFiltros={false}
                          value={emisionSeleccionada.cambioproductocertificado}
                          size="small"
                          nombreArchivo="cambios_productos_certificado"
                        >
                          <Column
                            header="Campo"
                            body={(cambio) =>
                              cambio.producto_x_productocampospropios
                                ?.productocampospropios?.etiqueta || "—"
                            }
                          />
                          <Column
                            header="Anterior"
                            body={(cambio) => cambio.valoranterior || "—"}
                          />
                          <Column
                            header="Nuevo"
                            body={(cambio) => cambio.valornuevo || "—"}
                          />
                        </TablaFiltrable>
                      </div>
                    )}

                    {emisionSeleccionada.productoalta?.length === 0 &&
                      emisionSeleccionada.productobaja?.length === 0 &&
                      emisionSeleccionada.cambioproductocertificado?.length ===
                        0 && (
                        <p className="text-500">
                          No hay cambios de productos en esta emisión.
                        </p>
                      )}
                  </div>
                )}
              </Sidebar>

              <Sidebar
                visible={sidebarCambioEstado}
                onHide={() => {
                  setSidebarCambioEstado(false);
                  setEstadoSeleccionado(null);
                }}
                position="right"
                className="w-full lg:w-30rem md:w-30rem"
              >
                <h2>Cambiar estado del certificado</h2>
                {estadosDropdown && (
                  <>
                    <Formulario
                      config={{
                        secciones: [
                          {
                            campos: [
                              {
                                nombre: "nuevoestadoid",
                                tipoitemformularioid: 5,
                                etiqueta: "Nuevo estado",
                                requerido: true,
                                opciones: estadosDropdown,
                                sinOpcionInicial: true,
                                onChange: (value) => {
                                  const estado = estadosDropdown.find(
                                    (e) => e.value === value
                                  );
                                  setEstadoSeleccionado(estado || null);
                                }
                              },
                              {
                                nombre: "notas",
                                tipoitemformularioid: 11,
                                etiqueta: "Notas",
                                requerido: true
                              }
                            ]
                          }
                        ],
                        botones: [
                          {
                            etiqueta: "Cambiar estado",
                            callback: handleCambioEstado,
                            validar: true,
                            confirmacion:
                              "¿Está seguro de cambiar el estado del certificado?"
                          }
                        ]
                      }}
                      loader={loaderCambioEstado}
                      key={cambiosEstado.length}
                    />
                    {estadoSeleccionado?.vigentebool && (
                      <Message
                        severity="success"
                        text="Este cambio de estado hará que el certificado sea visible en la plataforma pública de certificación."
                        css={{ width: "100%" }}
                      />
                    )}
                    {estadoSeleccionado?.bajadefinitiva && (
                      <Message
                        severity="error"
                        icon="pi pi-exclamation-triangle"
                        text="Luego de entrar en este estado, el certificado no puede volver a ser puesto en vigencia."
                        css={{ width: "100%" }}
                      />
                    )}
                  </>
                )}
              </Sidebar>

              <Sidebar
                visible={sidebarBajaUrsea}
                onHide={() => {
                  setSidebarBajaUrsea(false);
                  setCambioEstadoParaBaja(null);
                }}
                position="right"
                className="w-full lg:w-30rem md:w-30rem"
              >
                <h2>Notificar baja a URSEA</h2>
                <Formulario
                  config={{
                    secciones: [
                      {
                        campos: [
                          {
                            nombre: "motivo",
                            tipoitemformularioid: 5,
                            etiqueta: "Motivo",
                            requerido: true,
                            sinOpcionInicial: true,
                            opciones: [
                              { value: "comercial", etiqueta: "Comercial" },
                              { value: "técnico", etiqueta: "Técnico" },
                              { value: "error", etiqueta: "Error" },
                              { value: "otro", etiqueta: "Otro" }
                            ]
                          }
                        ]
                      }
                    ],
                    botones: [
                      {
                        etiqueta: "Enviar baja a URSEA",
                        callback: handleBajaUrsea,
                        validar: true,
                        confirmacion:
                          "¿Está seguro de enviar la baja a URSEA?"
                      }
                    ]
                  }}
                  loader={loaderBajaUrsea}
                  key={cambioEstadoParaBaja?.id}
                />
              </Sidebar>

              <Card css={{ marginTop: "1em" }}>
                <Titulo
                  botones={
                    <Boton
                      texto
                      negro
                      callback={() => setSidebarCambioEstado(true)}
                    >
                      <i className="pi pi-plus" />
                    </Boton>
                  }
                >
                  Cambios de estado
                </Titulo>
                {cambiosEstado.length > 0 && (
                  <Timeline
                    value={cambiosEstado}
                    opposite={(cambio) => (
                      <div css={{ marginTop: "10px" }}>
                        {formatFecha(cambio.fechacambio)}
                      </div>
                    )}
                    content={(cambio) => (
                      <Card className="mb-3">
                        <div
                          css={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "0.75em"
                          }}
                        >
                          <div
                            css={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.5em"
                            }}
                          >
                            <strong>{cambio.nuevoestado.nombre}</strong>
                            <Tag
                              value={
                                cambio.nuevoestado.vigentebool
                                  ? "Vigente"
                                  : cambio.nuevoestado.bajadefinitiva
                                    ? "Baja definitiva"
                                    : "No vigente"
                              }
                              severity={
                                cambio.nuevoestado.vigentebool
                                  ? "success"
                                  : cambio.nuevoestado.bajadefinitiva
                                    ? "danger"
                                    : null
                              }
                              css={
                                !cambio.nuevoestado.vigentebool &&
                                !cambio.nuevoestado.bajadefinitiva
                                  ? {
                                      backgroundColor: "var(--amarillo)",
                                      color: "white"
                                    }
                                  : undefined
                              }
                            />
                          </div>
                          {cambio.notas && (
                            <small className="text-500">{cambio.notas}</small>
                          )}
                          <small className="text-500">
                            <strong>Por:</strong>{" "}
                            {cambio.usuario
                              ? `${cambio.usuario.nombre} ${cambio.usuario.apellido}`
                              : "Sistema (automático)"}
                          </small>
                          {cambio.fechabajaursea && (
                            <small className="text-500">
                              <strong>Baja URSEA:</strong>{" "}
                              {formatFecha(cambio.fechabajaursea)}
                            </small>
                          )}
                          {cambio.nuevoestado.bajadefinitiva &&
                            certificado.proceso.certificadotipo
                              .gestionadoursea &&
                            !cambio.fechabajaursea && (
                              <div>
                                <Boton
                                  texto
                                  rojo
                                  chico
                                  callback={() => {
                                    setCambioEstadoParaBaja(cambio);
                                    setSidebarBajaUrsea(true);
                                  }}
                                >
                                  Notificar baja a URSEA
                                </Boton>
                              </div>
                            )}
                        </div>
                      </Card>
                    )}
                    marker={(cambio) => (
                      <span
                        css={{
                          display: "flex",
                          width: "2rem",
                          height: "2rem",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "50%",
                          backgroundColor: cambio.nuevoestado.vigentebool
                            ? "var(--verde)"
                            : cambio.nuevoestado.bajadefinitiva
                              ? "var(--rojo)"
                              : "var(--amarillo)",
                          color: "white",
                          fontSize: "0.8rem"
                        }}
                      >
                        <i
                          className={
                            cambio.nuevoestado.vigentebool
                              ? "pi pi-check"
                              : cambio.nuevoestado.bajadefinitiva
                                ? "pi pi-times"
                                : "pi pi-exclamation-triangle"
                          }
                        />
                      </span>
                    )}
                  />
                )}
              </Card>
            </div>
          </div>

          <Sidebar
            visible={sidebarAcciones}
            onHide={() => setSidebarAcciones(false)}
            position="right"
            className="w-full lg:w-30rem md:w-30rem"
          >
            <h2>Acciones</h2>
            <div
              css={{
                display: "flex",
                flexDirection: "column",
                gap: "1em"
              }}
            >
              <div>
                <p className="text-500" css={{ marginBottom: "0.5em" }}>
                  Genera una nueva extensión del certificado para otro cliente.
                </p>
                <Boton
                  callback={() => {
                    setSidebarAcciones(false);
                    setSidebarExtension(true);
                  }}
                >
                  Generar extensión
                </Boton>
              </div>
              <div>
                <p className="text-500" css={{ marginBottom: "0.5em" }}>
                  Vuelve a generar el PDF del certificado de la última emisión.
                  No genera una nueva emisión.
                </p>
                <Boton
                  callback={regenerarCertificado}
                  loader={loaderRegenerarCertificado}
                  confirmacion="¿Está seguro de regenerar el certificado?"
                >
                  Regenerar certificado
                </Boton>
              </div>
              <div>
                <p className="text-500" css={{ marginBottom: "0.5em" }}>
                  Vuelve a generar las etiquetas de los productos del
                  certificado.
                </p>
                <Boton
                  callback={regenerarEtiquetas}
                  loader={loaderRegenerarEtiquetas}
                  confirmacion="¿Está seguro de regenerar las etiquetas?"
                >
                  Regenerar etiquetas
                </Boton>
              </div>
              <div>
                <p className="text-500" css={{ marginBottom: "0.5em" }}>
                  Genera una nueva emisión del certificado.
                </p>
                <Boton
                  callback={() => {
                    setSidebarAcciones(false);
                    setSidebarReemision(true);
                  }}
                >
                  Reemitir certificado
                </Boton>
              </div>
            </div>
          </Sidebar>

          {/* Re-emisión del certificado */}
          <Sidebar
            visible={sidebarReemision}
            onHide={() => setSidebarReemision(false)}
            position="right"
            className="w-full lg:w-30rem md:w-30rem"
          >
            <Titulo>Reemitir certificado</Titulo>
            <Formulario
              config={{
                secciones: [
                  {
                    campos: [
                      {
                        nombre: "fechaemision",
                        etiqueta:
                          "Fecha de emisión (si queda en blanco es hoy)",
                        tipoitemformularioid: 7
                      },
                      {
                        nombre: "notas",
                        etiqueta: "Comentario",
                        tipoitemformularioid: 11,
                        requerido: true
                      },
                      {
                        nombre: "actividadid",
                        tipoitemformularioid: 5,
                        etiqueta: "Actividad relacionada a la emisión",
                        requerido: true,
                        opciones: `admin/actividad/proceso/${certificado.proceso.id}?correspondeemision=true&dropdown=true`,
                        sinOpcionInicial: true
                      }
                    ]
                  }
                ],
                botones: [
                  {
                    etiqueta: "Reemitir certificado",
                    callback: reemitirCertificado,
                    confirmacion:
                      "¿Está seguro de reemitir el certificado?",
                    validar: true
                  }
                ]
              }}
              loader={loaderReemision}
              key={emisiones?.length}
            />
          </Sidebar>
        </>
      )}
    </>
  );
}
